/**
 * HDS Web Vitals bridge.
 *
 * The web-vitals library owns the PerformanceObserver details. This wrapper
 * only turns the library callbacks into one Shopify Analytics event per full
 * page visit and keeps the event queued until Shopify Analytics is available.
 */
(function () {
  'use strict';

  if (window.__hdsWebVitalsBridgeLoaded) return;
  window.__hdsWebVitalsBridgeLoaded = true;

  var script = document.currentScript;
  var WEB_VITALS_EVENT = 'hds:web_vitals-publish';
  var PAGE_NOT_FOUND_EVENT = 'hds:page_not_found-publish';
  var RETRY_DELAY_MS = 250;
  var RETRY_LIMIT = 40;
  var MAX_QUEUE_SIZE = 20;

  var metricValues = {};
  var navigationType = null;
  var metricListenersRegistered = false;
  var publishRequested = false;
  var pageNotFoundRequested = false;
  var publishQueue = [];
  var retryTimer = null;
  var visitContext = readVisitContext();

  function isFiniteNumber(value) {
    return typeof value === 'number' && isFinite(value) && !isNaN(value);
  }

  function normalizeMetric(name, value) {
    if (!isFiniteNumber(value)) return undefined;

    if (name === 'cls') return Number(value.toFixed(4));

    return Math.round(value);
  }

  function readVisitContext() {
    var location = window.location;
    var pageType = script && script.getAttribute('data-hds-page-type');
    var pageLocation = location && location.href ? String(location.href) : '';
    var pagePath = location && location.pathname ? String(location.pathname) : '';
    var pageReferrer = document.referrer ? String(document.referrer) : '';

    return {
      page_type: pageType || 'other',
      page_location: pageLocation,
      page_path: pagePath,
      page_referrer: pageReferrer,
    };
  }

  function canPublish() {
    return !!(
      window.Shopify &&
      window.Shopify.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    );
  }

  function scheduleQueueDrain() {
    if (retryTimer) return;

    retryTimer = window.setTimeout(function () {
      retryTimer = null;
      drainQueue();
    }, RETRY_DELAY_MS);
  }

  function publishOne(item) {
    if (!canPublish()) return false;

    try {
      window.Shopify.analytics.publish(item.name, item.payload);
      return true;
    } catch (error) {
      return false;
    }
  }

  function drainQueue() {
    if (!publishQueue.length) return;

    if (!canPublish()) {
      scheduleQueueDrain();
      return;
    }

    while (publishQueue.length) {
      var item = publishQueue[0];

      if (publishOne(item)) {
        publishQueue.shift();
        continue;
      }

      item.tries += 1;

      if (item.tries >= RETRY_LIMIT) {
        publishQueue.shift();
        continue;
      }

      scheduleQueueDrain();
      return;
    }
  }

  function enqueue(name, payload) {
    if (publishQueue.length >= MAX_QUEUE_SIZE) publishQueue.shift();

    publishQueue.push({ name: name, payload: payload, tries: 0 });
    drainQueue();
  }

  function metricPayload() {
    var payload = {
      page_type: visitContext.page_type,
      page_location: visitContext.page_location,
      page_path: visitContext.page_path,
      page_referrer: visitContext.page_referrer,
    };
    // navigate | reload | back-forward | back-forward-cache | prerender | restore
    // (web-vitals). Splits bfcache restores from full loads per browser.
    if (navigationType) payload.navigation_type = navigationType;
    var names = ['lcp', 'fcp', 'ttfb', 'cls', 'inp'];

    for (var i = 0; i < names.length; i++) {
      var name = names[i];

      // An unavailable metric is intentionally omitted. In particular, INP
      // must not be represented as 0 when the visit had no interaction.
      if (Object.prototype.hasOwnProperty.call(metricValues, name)) {
        payload[name] = metricValues[name];
      }
    }

    return payload;
  }

  function recordMetric(name, metric) {
    if (!metric) return;

    var value = normalizeMetric(name, metric.value);

    if (value === undefined) return;

    metricValues[name] = value;
    if (typeof metric.navigationType === 'string') navigationType = metric.navigationType;
  }

  function registerMetricListeners() {
    var webVitals = window.webVitals;

    if (metricListenersRegistered) return true;
    if (!webVitals) return false;

    if (
      typeof webVitals.onLCP !== 'function' ||
      typeof webVitals.onFCP !== 'function' ||
      typeof webVitals.onTTFB !== 'function' ||
      typeof webVitals.onCLS !== 'function' ||
      typeof webVitals.onINP !== 'function'
    ) {
      return false;
    }

    metricListenersRegistered = true;

    // Keep the latest candidate available for the pagehide fallback too. The
    // library's default LCP callback waits for hidden/interaction, while a
    // Shopify navigation can expose pagehide without a preceding hidden event.
    webVitals.onLCP(
      function (metric) {
        recordMetric('lcp', metric);
      },
      { reportAllChanges: true },
    );
    webVitals.onFCP(function (metric) {
      recordMetric('fcp', metric);
    });
    webVitals.onTTFB(function (metric) {
      recordMetric('ttfb', metric);
    });
    webVitals.onCLS(function (metric) {
      recordMetric('cls', metric);
    });
    webVitals.onINP(function (metric) {
      recordMetric('inp', metric);
    });

    return true;
  }

  function retryMetricRegistration() {
    if (registerMetricListeners()) return;

    window.setTimeout(function () {
      retryMetricRegistration();
    }, RETRY_DELAY_MS);
  }

  function flushMetrics() {
    if (publishRequested || document.prerendering) return;

    publishRequested = true;
    enqueue(WEB_VITALS_EVENT, metricPayload());
  }

  function publishPageNotFound() {
    if (document.prerendering) {
      document.addEventListener('prerenderingchange', publishPageNotFound, { once: true });
      return;
    }
    if (pageNotFoundRequested) return;

    pageNotFoundRequested = true;
    enqueue(PAGE_NOT_FOUND_EVENT, {
      page_type: '404',
      page_location: visitContext.page_location,
      page_path: visitContext.page_path,
      page_referrer: visitContext.page_referrer,
    });
  }

  function resetForBfcacheVisit() {
    metricValues = {};
    navigationType = null;
    metricListenersRegistered = false;
    publishRequested = false;
    pageNotFoundRequested = false;
    visitContext = readVisitContext();
    retryMetricRegistration();

    if (script && script.getAttribute('data-hds-page-not-found') === 'true') {
      publishPageNotFound();
    }
  }

  retryMetricRegistration();

  if (script && script.getAttribute('data-hds-page-not-found') === 'true') {
    publishPageNotFound();
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden' || document.hidden) {
      // web-vitals registers its lifecycle listeners before this bridge. The
      // tick lets its final CLS/INP/LCP callback update metricValues first.
      window.setTimeout(flushMetrics, 0);
    }
  });

  window.addEventListener('pagehide', flushMetrics);
  window.addEventListener('beforeunload', flushMetrics);

  window.addEventListener('pageshow', function (event) {
    if (event && event.persisted) resetForBfcacheVisit();
  });
})();
